<template>
  <div class="stock-summary-container">
    <div class="summary-layout">
      <div class="article-list">
        <!-- 顶部 Header 区域 -->
        <div class="content-header">
          <div class="content-header__title-area">
            <div class="content-header__icon" @click="router.push('/login');">
              <el-icon><Notebook /></el-icon>
            </div>
            <div class="content-header__text">
              <h1 class="content-header__title">{{ currentUser ? currentUser.username : '经书' }}</h1>
              <span class="content-header__subtitle">{{ searchResultCount }} 条</span>
            </div>
          </div>
          <el-button type="primary" class="content-header__create-btn" @click="createNewSummary">
            <el-icon><Plus /></el-icon>
            新建
          </el-button>
        </div>

        <!-- 搜索和工具栏 -->
        <div class="summary-list__toolbar">
          <el-input
            v-model="searchQuery"
            placeholder="搜索文章标题或内容..."
            class="summary-list__search"
            clearable
          >
            <template #prefix>
              <el-icon><Search /></el-icon>
            </template>
          </el-input>
          
          <div class="summary-list__actions">
            <el-tooltip content="视图切换" placement="bottom">
              <div class="view-toggle-group">
                <div 
                  class="view-toggle-item" 
                  :class="{ active: isGridView }"
                  @click="setViewMode('grid')"
                >
                  <el-icon><Grid /></el-icon>
                </div>
                <div 
                  class="view-toggle-item" 
                  :class="{ active: !isGridView }"
                  @click="setViewMode('list')"
                >
                  <el-icon><List /></el-icon>
                </div>
              </div>
            </el-tooltip>

            <el-dropdown trigger="click" @command="handleSortCommand">
              <div class="action-btn">
                <el-icon><Sort /></el-icon>
              </div>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="updated_at">最新更新</el-dropdown-item>
                  <el-dropdown-item command="created_at">创建时间</el-dropdown-item>
                  <el-dropdown-item command="title">标题排序</el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>

            <el-dropdown trigger="click" @command="handleCategoryCommand">
              <div class="action-btn">
                <el-icon><Filter /></el-icon>
              </div>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item command="all">全部文章</el-dropdown-item>
                  <el-dropdown-item 
                    v-for="cat in categories" 
                    :key="cat.name" 
                    :command="cat.name"
                  >
                    {{ cat.name }} ({{ cat.count }})
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
            
            <div class="action-btn" @click="openCategoryManager">
              <el-icon><Setting /></el-icon>
            </div>
          </div>
        </div>

        <div 
          class="scrollbar-wrapper"
          @touchstart="handleTouchStart"
          @touchmove="handleTouchMove"
          @touchend="handleTouchEnd"
        >
          <el-scrollbar class="article-scrollbar" ref="scrollbarRef" @scroll="handleScroll">
            <!-- 下拉刷新指示器 -->
            <div 
              class="pull-refresh-indicator"
              :style="{ height: `${Math.max(pullDistance, 0)}px`, opacity: pullDistance > 0 || isRefreshing ? 1 : 0 }"
            >
              <div class="refresh-content" v-if="pullDistance > 0 || isRefreshing">
                <el-icon :class="{ 'is-loading': isRefreshing, 'rotate-icon': pullDistance >= PULL_THRESHOLD && !isRefreshing }">
                  <Refresh />
                </el-icon>
                <span class="refresh-text">
                  {{ isRefreshing ? '正在刷新...' : (pullDistance >= PULL_THRESHOLD ? '释放刷新' : '下拉刷新') }}
                </span>
              </div>
            </div>

            <div v-if="isGridView" class="summary-list__grid">
              <div
                v-for="(summary, index) in visibleSummaries"
                :key="summary.id"
                class="summary-list__card"
                :style="{ backgroundColor: getCardColor(index) }"
                v-lazy="summary.id"
                v-ripple
                @click="selectSummary(summary.id)"
              >
                <div v-if="isSummaryVisible(summary.id)" class="summary-list__card-body">
                  <div class="summary-list__card-header">
                    <h5 class="summary-list__card-title" :title="summary.title">{{ summary.title }}</h5>
                  </div>
                  <div class="summary-list__card-meta">
                    <span class="summary-list__card-date">
                      <el-icon><Clock /></el-icon>
                      {{ formatDate(summary.updated_at) }}
                    </span>
                    <span class="summary-list__card-category" :style="{ backgroundColor: getCategoryColor(summary.categories) }">
                      {{ summary.categories }}
                    </span>
                  </div>
                  <div
                    class="summary-list__card-preview"
                    :title="summary.content.replace(/<[^>]*>/g, '')"
                    v-html="getPreviewText(summary.content)"
                  ></div>
                </div>
                <div v-else class="summary-list__card-skeleton">
                  <div class="summary-list__card-skeleton-line"></div>
                  <div class="summary-list__card-skeleton-line summary-list__card-skeleton-line--short"></div>
                  <div class="summary-list__card-skeleton-block"></div>
                </div>
                <div class="summary-list__card-actions" @click.stop>
                  <el-button size="small" text v-ripple @click="handleCardShare(summary)">
                    分享
                  </el-button>
                </div>
              </div>
            </div>

            <div v-else class="summary-list__list">
              <div
                v-for="summary in visibleSummaries"
                :key="summary.id"
                class="summary-list__list-item article-item"
                v-lazy="summary.id"
                v-ripple
                @click="selectSummary(summary.id)"
              >
                <div class="article-content">
                  <div class="article-header">
                    <h5 class="article-title" :title="summary.title">{{ summary.title }}</h5>
                  </div>
                  <div class="article-meta">
                    <span class="article-date">
                      <el-icon><Clock /></el-icon>
                      {{ formatDate(summary.updated_at) }}
                    </span>
                    <span class="article-category" :style="{ backgroundColor: getCategoryColor(summary.categories) }">
                      {{ summary.categories }}
                    </span>
                  </div>
                  <div
                    class="article-preview"
                    :title="summary.content.replace(/<[^>]*>/g, '')"
                    v-html="getPreviewText(summary.content)"
                  ></div>
                </div>
                <div class="article-actions">
                  <el-icon class="arrow-icon"><ArrowRight /></el-icon>
                </div>
              </div>
            </div>
          </el-scrollbar>
        </div>
      </div>
    </div>
    
    <!-- 对话框 -->
    <el-dialog
      v-model="titleDialogVisible"
      title="新建文章"
      :width="isMobile ? '90%' : '40%'"
      center
      custom-class="create-summary-dialog"
    >
      <div class="dialog-content">
        <el-form :model="formData" :rules="formRules" ref="formRef" label-width="80px">
          <el-form-item label="文章标题" prop="title">
            <el-input
              v-model="formData.title"
              placeholder="请输入文章标题"
              maxlength="100"
              show-word-limit
            />
          </el-form-item>
          <el-form-item label="文章分类" prop="categories">
            <el-select 
              v-model="formData.categories" 
              placeholder="请选择文章分类"
              allow-create
              filterable
              style="width: 100%"
            >
              <el-option
                v-for="category in categories"
                :key="category.name"
                :label="category.name"
                :value="category.name"
              />
            </el-select>
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="titleDialogVisible = false" :size="isMobile ? 'large' : 'default'">取消</el-button>
          <el-button type="primary" @click="confirmCreateSummary" :size="isMobile ? 'large' : 'default'">确定</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 分类管理对话框 -->
    <el-dialog
      v-model="categoryManagerVisible"
      title="分类管理"
      :width="isMobile ? '95%' : '60%'"
      center
      custom-class="category-manager-dialog"
    >
      <div class="category-manager-content">
        <div class="manager-header">
          <el-button type="primary" size="small" @click="createCategoryItem">
            <el-icon><Plus /></el-icon> 新增分类
          </el-button>
        </div>
        <el-table :data="categoryList" style="width: 100%" stripe border>
          <el-table-column prop="name" label="分类名称" />
          <el-table-column prop="color" label="颜色" width="100">
            <template #default="scope">
              <div class="color-preview" :style="{ backgroundColor: scope.row.color }"></div>
              {{ scope.row.color }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="150" align="center">
            <template #default="scope">
              <el-button size="small" @click="editCategoryItem(scope.row)">
                <el-icon><Edit /></el-icon>
              </el-button>
              <el-button size="small" type="danger" @click="deleteCategoryItem(scope.row)">
                <el-icon><Delete /></el-icon>
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="categoryManagerVisible = false">关闭</el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 新增/编辑分类对话框 -->
    <el-dialog
      v-model="categoryDialogVisible"
      :title="isCategoryEdit ? '编辑分类' : '新增分类'"
      :width="isMobile ? '90%' : '30%'"
      center
    >
      <el-form :model="categoryFormData" :rules="categoryFormRules" ref="categoryFormRef" label-width="80px">
        <el-form-item label="分类名称" prop="name">
          <el-input v-model="categoryFormData.name" placeholder="请输入分类名称" />
        </el-form-item>
        <el-form-item label="颜色" prop="color">
          <el-color-picker v-model="categoryFormData.color" />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="categoryDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmCategory">确定</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount, computed, watch, nextTick, onActivated } from 'vue';
import { useRouter } from 'vue-router';
import { getAllSummaries, createSummary, deleteSummary } from '../api/summary';
import { getAllCategories, createCategory, updateCategory, deleteCategory } from '../api/category';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Document, Plus, Clock, ArrowRight, Edit, Setting, Delete, Search, Grid, List, Sort, Filter, Notebook, Refresh } from '@element-plus/icons-vue';
import { isLoggedIn, getUser } from '../api/auth';
import { useStore } from 'vuex';

// 响应式数据
const router = useRouter();
const titleDialogVisible = ref(false);
const categoryManagerVisible = ref(false);
const categoryDialogVisible = ref(false);
const formRef = ref(null);
const categoryFormRef = ref(null);
const selectedCategory = ref('all');
const sortBy = ref('updated_at');
const formData = ref({
  title: '',
  categories: ''
});

const categoryFormData = ref({
  id: '',
  name: '',
  color: '#3b82f6',
  icon: 'Document'
});

const formRules = {
  title: [
    { required: true, message: '请输入文章标题', trigger: 'blur' },
    { min: 2, max: 50, message: '标题长度应在2-50个字符之间', trigger: 'blur' }
  ],
  categories: [
    { required: true, message: '请选择文章分类', trigger: 'change' }
  ]
};

const categoryFormRules = {
  name: [
    { required: true, message: '请输入分类名称', trigger: 'blur' },
    { min: 2, max: 20, message: '分类名称长度应在2-20个字符之间', trigger: 'blur' }
  ],
  color: [
    { required: true, message: '请选择分类颜色', trigger: 'change' }
  ]
};

const summaries = ref([]);
const categoryList = ref([]);
const isMobile = ref(window.innerWidth <= 768);
const userLoggedIn = computed(() => isLoggedIn());
const currentUser = computed(() => getUser());
const isCategoryEdit = ref(false);
const searchQuery = ref('');

const store = useStore();

const viewMode = computed({
  get() {
    return store.getters.summaryViewMode;
  },
  set(mode) {
    store.commit('setSummaryViewMode', mode);
  }
});

const isGridView = computed(() => viewMode.value === 'grid');

const VIRTUAL_CHUNK_SIZE = 30;
const visibleCount = ref(VIRTUAL_CHUNK_SIZE);
const scrollbarRef = ref(null);
const savedScrollTop = ref(0);

// 下拉刷新状态
const isPulling = ref(false);
const isRefreshing = ref(false);
const pullDistance = ref(0);
const startY = ref(0);
const PULL_THRESHOLD = 80; // 触发刷新的阈值
const MAX_PULL_DISTANCE = 120; // 最大下拉距离

// 分类配置
const categoryConfigMap = computed(() => {
  const map = {};
  categoryList.value.forEach(cat => {
    map[cat.name] = { color: cat.color, icon: Document }; // 暂时统一使用 Document 图标
  });
  return map;
});

// 计算属性
const categories = computed(() => {
  const categoryMap = new Map();
  
  // 统计文章数量
  summaries.value.forEach(summary => {
    const category = summary.categories;
    if (category) {
      if (categoryMap.has(category)) {
        categoryMap.set(category, categoryMap.get(category) + 1);
      } else {
        categoryMap.set(category, 1);
      }
    }
  });
  
  // 合并后台配置的分类
  categoryList.value.forEach(cat => {
    if (!categoryMap.has(cat.name)) {
      categoryMap.set(cat.name, 0);
    }
  });
  
  return Array.from(categoryMap.entries()).map(([name, count]) => ({
    name,
    count,
    color: categoryConfigMap.value[name]?.color || '#64748b',
    icon: categoryConfigMap.value[name]?.icon || 'Document',
    id: categoryList.value.find(c => c.name === name)?.id
  }));
});

const filteredSummaries = computed(() => {
  if (selectedCategory.value === 'all') {
    return summaries.value;
  }
  return summaries.value.filter(summary => summary.categories === selectedCategory.value);
});

const searchedSummaries = computed(() => {
  const keyword = searchQuery.value.trim().toLowerCase();
  if (!keyword) {
    return filteredSummaries.value;
  }
  return filteredSummaries.value.filter(summary => {
    const title = (summary.title || '').toLowerCase();
    const contentText = summary.content ? summary.content.replace(/<[^>]*>/g, '').toLowerCase() : '';
    return title.includes(keyword) || contentText.includes(keyword);
  });
});

const sortedSummaries = computed(() => {
  const sorted = [...searchedSummaries.value];
  
  switch (sortBy.value) {
    case 'updated_at':
      return sorted.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    case 'created_at':
      return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    case 'title':
      return sorted.sort((a, b) => a.title.localeCompare(b.title));
    default:
      return sorted;
  }
});

const searchResultCount = computed(() => sortedSummaries.value.length);

const visibleSummaries = computed(() => {
  if (sortedSummaries.value.length === 0) {
    return [];
  }
  if (visibleCount.value >= sortedSummaries.value.length) {
    return sortedSummaries.value;
  }
  return sortedSummaries.value.slice(0, visibleCount.value);
});



// 方法
const setViewMode = (mode) => {
  viewMode.value = mode;
};

const loadSummaries = async (isRefresh = false) => {
  try {
    const { data } = await getAllSummaries();
    summaries.value = data.data;
    if (isRefresh) {
      ElMessage.success('刷新成功');
    }
  } catch (error) {
    ElMessage.error('加载列表失败');
    console.error('加载列表失败:', error);
  } finally {
    if (isRefresh) {
      isRefreshing.value = false;
      pullDistance.value = 0;
    }
  }
};

const loadCategories = async () => {
  try {
    const { data } = await getAllCategories();
    categoryList.value = data.data;
  } catch (error) {
    ElMessage.error('加载分类配置失败');
    console.error('加载分类配置失败:', error);
  }
};

const selectCategory = (category) => {
  selectedCategory.value = category;
};

// 滚动处理：实现虚拟滚动加载
const handleScroll = () => {
  const scrollbar = scrollbarRef.value;
  if (!scrollbar || !scrollbar.wrapRef) return;
  const wrap = scrollbar.wrapRef;
  const { scrollTop, clientHeight, scrollHeight } = wrap;
  
  savedScrollTop.value = scrollTop;

  // 检查是否滚动到底部（阈值100px）
  const nearBottom = scrollTop + clientHeight + 100 >= scrollHeight;
  
  // 如果接近底部且还有更多数据，加载下一页
  if (nearBottom && visibleCount.value < sortedSummaries.value.length) {
    visibleCount.value = Math.min(
      visibleCount.value + VIRTUAL_CHUNK_SIZE,
      sortedSummaries.value.length
    );
  }
};

const selectSummary = (id) => {
  router.push({ name: 'ArticleDetail', params: { id } });
};

const getCategoryColor = (category) => {
  return categoryConfigMap.value[category]?.color || '#64748b';
};

// 下拉刷新逻辑
const handleTouchStart = (e) => {
  if (!isMobile.value || isRefreshing.value) return;
  
  // 只有当滚动条在顶部时才启用下拉
  const scrollTop = scrollbarRef.value?.wrapRef?.scrollTop || 0;
  if (scrollTop === 0) {
    startY.value = e.touches[0].clientY;
    isPulling.value = true;
  } else {
    isPulling.value = false;
  }
};

const handleTouchMove = (e) => {
  if (!isPulling.value || isRefreshing.value) return;
  
  const currentY = e.touches[0].clientY;
  const distance = currentY - startY.value;
  
  // 只有向下拉动且距离大于0才处理
  if (distance > 0) {
    // 增加阻尼效果
    pullDistance.value = Math.min(distance * 0.5, MAX_PULL_DISTANCE);
    
    // 如果正在下拉，阻止默认滚动行为（防止整个页面下拉）
    if (pullDistance.value > 10 && e.cancelable) {
      e.preventDefault();
    }
  } else {
    pullDistance.value = 0;
  }
};

const handleTouchEnd = () => {
  if (!isPulling.value || isRefreshing.value) return;
  
  isPulling.value = false;
  
  if (pullDistance.value >= PULL_THRESHOLD) {
    isRefreshing.value = true;
    // 保持在阈值位置
    pullDistance.value = PULL_THRESHOLD;
    loadSummaries(true);
  } else {
    // 回弹
    pullDistance.value = 0;
  }
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getPreviewText = (content) => {
  // 移除HTML标签并截取前100个字符
  const text = content.replace(/<[^>]*>/g, '').substring(0, 100);
  return text + (text.length >= 100 ? '...' : '');
};



const createNewSummary = () => {
  if (!userLoggedIn.value) {
    ElMessage.warning('请先登录后再创建文章');
    return;
  }
  
  titleDialogVisible.value = true;
  formData.value.title = '';
  formData.value.categories = selectedCategory.value === 'all' ? '' : selectedCategory.value;
  if (formRef.value) {
    formRef.value.resetFields();
  }
};

const openCategoryManager = () => {
  if (!userLoggedIn.value) {
    ElMessage.warning('请先登录后再管理分类');
    return;
  }
  categoryManagerVisible.value = true;
};

const createCategoryItem = () => {
  categoryDialogVisible.value = true;
  isCategoryEdit.value = false;
  categoryFormData.value = {
    id: '',
    name: '',
    color: '#3b82f6',
    icon: 'Document'
  };
  if (categoryFormRef.value) {
    categoryFormRef.value.resetFields();
  }
};

const editCategoryItem = (item) => {
  categoryDialogVisible.value = true;
  isCategoryEdit.value = true;
  categoryFormData.value = {
    id: item.id,
    name: item.name,
    color: item.color,
    icon: item.icon || 'Document'
  };
  if (categoryFormRef.value) {
    categoryFormRef.value.resetFields();
  }
};

const deleteCategoryItem = async (item) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除分类"${item.name}"吗？`,
      '警告',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }
    );
    
    await deleteCategory(item.id);
    ElMessage.success('删除成功');
    loadCategories();
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('删除失败');
      console.error('删除失败:', error);
    }
  }
};

const confirmCategory = async () => {
  if (!categoryFormRef.value) return;
  
  try {
    await categoryFormRef.value.validate();
    
    if (isCategoryEdit.value) {
      await updateCategory(categoryFormData.value.id, {
        name: categoryFormData.value.name,
        color: categoryFormData.value.color,
        icon: categoryFormData.value.icon
      });
      ElMessage.success('更新分类成功');
    } else {
      await createCategory({
        name: categoryFormData.value.name,
        color: categoryFormData.value.color,
        icon: categoryFormData.value.icon
      });
      ElMessage.success('创建分类成功');
    }
    
    categoryDialogVisible.value = false;
    loadCategories();
  } catch (error) {
    ElMessage.error(isCategoryEdit.value ? '更新分类失败' : '创建分类失败');
    console.error('操作失败:', error);
  }
};

const confirmCreateSummary = async () => {
  if (!formRef.value) return;
  
  try {
    await formRef.value.validate();
    const { data } = await createSummary({
      title: formData.value.title.trim(),
      content: '<p></p>',
      displayorder: 1,
      categories: formData.value.categories,
    });
    summaries.value.unshift(data.data);
    titleDialogVisible.value = false;
    selectedCategory.value = data.data.categories;
    router.push({ name: 'ArticleDetail', params: { id: data.data.id } });
  } catch (error) {
    ElMessage.error('创建文章失败');
    console.error('创建文章失败:', error);
  }
};

const handleCardEdit = (summary) => {
  router.push({ name: 'ArticleDetail', params: { id: summary.id } });
};

const handleCardDelete = async (summary) => {
  if (!userLoggedIn.value) {
    ElMessage.warning('请先登录后再删除文章');
    return;
  }

  try {
    await ElMessageBox.confirm(
      `确定要删除文章"${summary.title}"吗？`,
      '删除确认',
      {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }
    );

    await deleteSummary(summary.id);
    summaries.value = summaries.value.filter(s => s.id !== summary.id);

    ElMessage.success('删除文章成功');
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('删除文章失败');
      console.error('删除文章失败:', error);
    }
  }
};

const handleCardShare = async (summary) => {
  const shareData = {
    title: summary.title,
    text: getPreviewText(summary.content),
    url: `${window.location.origin}/article/${summary.id}`
  };

  try {
    if (navigator.share) {
      await navigator.share(shareData);
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(
        `${shareData.title}\n${shareData.text}\n${shareData.url}`
      );
      ElMessage.success('分享链接已复制到剪贴板');
    }
  } catch (error) {
    console.error('分享失败:', error);
  }
};

const handleSortCommand = (command) => {
  sortBy.value = command;
};

const handleCategoryCommand = (command) => {
  selectCategory(command);
};

const cardColors = [
  '#FEF3C7', // Amber
  '#DBEAFE', // Blue
  '#D1FAE5', // Emerald
  '#E0E7FF', // Indigo
  '#FEE2E2', // Red
  '#F3E8FF', // Purple
  '#FFEDD5', // Orange
  '#ECFCCB', // Lime
];

const getCardColor = (index) => {
  return cardColors[index % cardColors.length];
};

const visibleSummaryMap = ref({});

// 自定义指令：懒加载 (IntersectionObserver)
const vLazy = {
  mounted(el, binding) {
    const id = binding.value;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            // 当元素进入视口时标记为可见
            visibleSummaryMap.value[id] = true;
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.1 // 10% 可见时触发
      }
    );
    observer.observe(el);
    el._lazyObserver = observer;
  },
  unmounted(el) {
    if (el._lazyObserver) {
      el._lazyObserver.disconnect();
      delete el._lazyObserver;
    }
  }
};

const isSummaryVisible = (id) => {
  return !!visibleSummaryMap.value[id];
};

// 自定义指令：Material Design 波纹效果
const vRipple = {
  mounted(el) {
    const handler = (event) => {
      const rect = el.getBoundingClientRect();
      // 根据元素大小计算波纹尺寸
      const size = Math.max(rect.width, rect.height);
      const x = event.clientX - rect.left - size / 2;
      const y = event.clientY - rect.top - size / 2;

      const ripple = document.createElement('span');
      ripple.className = 'ripple-effect';
      ripple.style.width = `${size}px`;
      ripple.style.height = `${size}px`;
      ripple.style.left = `${x}px`;
      ripple.style.top = `${y}px`;

      el.classList.add('ripple-container');
      el.appendChild(ripple);

      // 动画结束后移除元素
      setTimeout(() => {
        ripple.remove();
      }, 400);
    };

    el.__rippleHandler__ = handler;
    el.addEventListener('click', handler);
  },
  unmounted(el) {
    if (el.__rippleHandler__) {
      el.removeEventListener('click', el.__rippleHandler__);
      delete el.__rippleHandler__;
    }
  }
};

onMounted(() => {
  loadSummaries();
  loadCategories();

  const resizeHandler = () => {
    isMobile.value = window.innerWidth <= 768;
  };

  window.addEventListener('resize', resizeHandler);

  onBeforeUnmount(() => {
    window.removeEventListener('resize', resizeHandler);
  });
  
  document.title = '藏经阁';
});

onActivated(() => {
  if (scrollbarRef.value) {
    scrollbarRef.value.setScrollTop(savedScrollTop.value);
  }
});

watch([searchQuery, selectedCategory, sortBy], () => {
  visibleCount.value = VIRTUAL_CHUNK_SIZE;
  if (scrollbarRef.value) {
    scrollbarRef.value.setScrollTop(0);
  }
});
</script>

<style scoped lang="scss">
$primary-color: #2c3e50;
$accent-color: #3b82f6;
$bg-color: #f8fafc;
$card-radius: 16px;
$transition-base: 0.3s cubic-bezier(0.4, 0, 0.2, 1);

/* Pastel Palette */
$colors: (
  1: #eef2ff, // Indigo
  2: #f0fdf4, // Green
  3: #fff1f2, // Rose
  4: #fffbeb, // Amber
  5: #fdf4ff, // Fuchsia
  6: #ecfeff, // Cyan
  7: #fefce8, // Yellow
  8: #f3f4f6  // Gray
);

.stock-summary-container {
  height: 100vh;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  background-color: $bg-color;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  overflow: hidden;
}

.summary-layout {
  display: flex;
  height: 100%;
  position: relative;
}

/* --- Left/Main Area (List/Grid) --- */
.article-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  transition: all $transition-base;
}

/* Header */
.content-header {
  padding: 4px 16px;
  background: rgba(255,255,255,0.8);
  backdrop-filter: blur(12px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(0,0,0,0.05);

  &__title-area {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__icon {
    width: 36px;
    height: 36px;
    background: $primary-color;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 26px;
    box-shadow: 0 8px 16px rgba(44,62,80,0.2);
  }

  &__text {
    display: flex;
    flex-direction: column;
  }

  &__title {
    font-size: 16px;
    font-weight: 800;
    color: $primary-color;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    font-size: 12px;
    color: #94a3b8;
    font-weight: 500;
  }

  &__create-btn {
    border-radius: 24px;
    padding: 10px 24px;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    transition: transform 0.2s;
    
    &:hover {
      transform: translateY(-2px);
    }
  }
}

/* Toolbar */
.summary-list__toolbar {
  padding: 6px 16px;
  display: flex;
  gap: 24px;
  align-items: center;
  
  .summary-list__search {
    width: 300px;
    
    :deep(.el-input__wrapper) {
      border-radius: 20px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.05);
      border: 1px solid transparent;
      background: white;
      transition: all 0.3s;
      
      &:hover, &.is-focus {
        box-shadow: 0 4px 12px rgba(0,0,0,0.08);
        border-color: $accent-color;
      }
    }
  }
  
  .summary-list__actions {
    display: flex;
    gap: 10px;
    margin-left: auto;
  }
}

.view-toggle-group {
  background: white;
  padding: 4px;
  border-radius: 12px;
  display: flex;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
  
  .view-toggle-item {
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    cursor: pointer;
    color: #94a3b8;
    transition: all 0.2s;
    
    &.active {
      background: $primary-color;
      color: white;
      box-shadow: 0 2px 8px rgba(44,62,80,0.2);
    }
  }
}

.action-btn {
  width: 32px;
  height: 32px;
  background: white;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #64748b;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-2px);
    color: $accent-color;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
}

/* Scroll Area */
.scrollbar-wrapper {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  touch-action: pan-y;
  overscroll-behavior-y: contain;
}

.article-scrollbar {
  flex: 1;
  /* padding moved to child containers to allow full-width refresh indicator */
}

/* Grid View */
.summary-list__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  padding: 0 16px 40px; /* Added padding here */
  align-items: start; /* For masonry-like feel */
}

.summary-list__card {
  background: white;
  border-radius: $card-radius;
  padding: 16px;
  cursor: pointer;
  position: relative;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 1px solid rgba(0,0,0,0.02);
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;

  /* Generate pastel backgrounds via nth-child roughly or just white */
  background: #ffffff; 
  /* The inline style in template :style="{ backgroundColor: getCardColor(index) }" handles the color */
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 20px 40px -10px rgba(0,0,0,0.1);
    z-index: 10;
    
    .summary-list__card-actions {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  &-title {
    font-size: 18px;
    font-weight: 700;
    color: $primary-color;
    margin: 0;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &-meta {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 12px;
  }

  &-date {
    display: flex;
    align-items: center;
    gap: 4px;
    color: rgba(0,0,0,0.5);
    font-weight: 500;
  }

  &-category {
    padding: 4px 10px;
    border-radius: 20px;
    background: rgba(255,255,255,0.5);
    color: #fff;
    font-weight: 600;
  }

  &-preview {
    font-size: 14px;
    color: #475569;
    line-height: 1.6;
    opacity: 0.9;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  
  &-actions {
    position: absolute;
    top: 16px;
    right: 16px;
    display: flex;
    opacity: 0;
    transform: translateY(-10px);
    transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
    
    .el-button {
      background: rgba(255,255,255,0.9);
      border-radius: 50%;
      width: 32px;
      height: 32px;
      padding: 0;
      border: none;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      
      &:hover {
        background: $primary-color;
        color: white;
      }
    }
  }
}

/* List View */
.summary-list__list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px 32px;
  
  &-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    background: white;
    border-radius: 16px;
    cursor: pointer;
    transition: all 0.2s;
    border: 1px solid rgba(0,0,0,0.02);
    
    &:hover {
      transform: translateX(8px);
      box-shadow: 0 8px 24px rgba(0,0,0,0.05);
    }
    
    &.active {
      border-left: 4px solid $accent-color;
      background: #f8fafc;
    }
    
    .article-content {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    
    .article-title {
      font-size: 16px;
      font-weight: 700;
      color: $primary-color;
      margin: 0;
    }
    
    .article-meta {
      display: flex;
      gap: 12px;
      font-size: 12px;
      color: #94a3b8;
    }

    .article-preview {
      font-size: 13px;
      color: #64748b;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      line-clamp: 1;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
}

/* Skeleton */
.summary-list__card-skeleton {
  padding: 24px;
  
  &-line {
    height: 24px;
    background: rgba(0,0,0,0.05);
    border-radius: 6px;
    margin-bottom: 12px;
    
    &--short {
      width: 60%;
    }
  }
  
  &-block {
    height: 80px;
    background: rgba(0,0,0,0.05);
    border-radius: 6px;
    margin-top: 16px;
  }
}

/* --- Right Area (Editor) --- */
/* Editor removed */


/* Responsive */
@media (max-width: 768px) {
  .content-header {
    gap: 16px;
    
    &__actions {
      width: 100%;
    }
    
    &__create-btn {
      justify-content: center;
    }
  }
  
  .summary-list__toolbar {
    align-items: stretch;
    
    .summary-list__search {
      width: 100%;
    }
  }
  
  .summary-list__grid {
    grid-template-columns: 1fr;
    padding: 16px;
  }
  
  .article-scrollbar {
    padding: 0;
  }
}

/* Pull to Refresh */
.pull-refresh-indicator {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  transition: height 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  background-color: #f8fafc;
}

.refresh-content {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #64748b;
  font-size: 14px;
  padding: 10px 0;
}

.rotate-icon {
  transform: rotate(180deg);
  transition: transform 0.3s;
}

@media (min-width: 769px) and (max-width: 1024px) {
  .summary-list__grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* Ripple Effect */
.ripple-container {
  position: relative;
  overflow: hidden;
}

.ripple-effect {
  position: absolute;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.1);
  transform: scale(0);
  animation: ripple 0.6s linear;
  pointer-events: none;
}

@keyframes ripple {
  to {
    transform: scale(4);
    opacity: 0;
  }
}
</style>

<style>
/* 保持原有全局样式 */
.view-content img {
  max-width: 100%;
  height: auto;
  margin: 10px 0;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.view-content h1,
.view-content h2,
.view-content h3 {
  margin: 20px 0 10px;
  font-weight: 600;
  color: #303133;
}

.view-content p {
  line-height: 1.8;
  margin-bottom: 10px;
  color: #606266;
}

.view-content blockquote {
  margin: 10px 0;
  padding: 10px 20px;
  background-color: #f4f4f5;
  border-left: 4px solid #909399;
  color: #606266;
}

.view-content pre {
  background-color: #f4f4f5;
  padding: 15px;
  border-radius: 4px;
  overflow-x: auto;
  margin: 10px 0;
}

.view-content code {
  font-family: Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
  background-color: #f4f4f5;
  padding: 2px 4px;
  border-radius: 2px;
  color: #c0392b;
}

.view-content table {
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0;
}

.view-content th,
.view-content td {
  border: 1px solid #dcdfe6;
  padding: 8px;
  text-align: left;
}

.view-content th {
  background-color: #f5f7fa;
}

.view-content a {
  color: #409eff;
  text-decoration: none;
}

.view-content a:hover {
  text-decoration: underline;
}

/* 自定义滚动条 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #dcdfe6;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #c0c4cc;
}
</style>
